import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, Clock, Search, ArrowLeft, IndianRupee,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

const TABS = [
  { key: "pending",  label: "Pending"  },
  { key: "verified", label: "Verified" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminDeposits() {
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, verified: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deposits", {
        params: { status, q: debouncedQ || undefined },
      });
      setItems(data.items || []);
      setCounts(data.counts || { pending: 0, verified: 0, rejected: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const note = action === "reject" ? window.prompt("Reason (optional):") || "" : "";
    try {
      await api.post(`/admin/deposits/${id}/${action}`, { note });
      toast.success(action === "verify" ? "Deposit verified & E-Wallet credited" : "Deposit rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050506" }}>
      <AdminHeader title="Fund Deposits" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Link
          to="/admin/dashboard"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-1 text-emerald-400 text-sm font-semibold"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <section className="rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-white/10">
            <div className="flex gap-2 flex-wrap">
              {TABS.map((t) => {
                const active = status === t.key;
                return (
                  <button
                    key={t.key}
                    data-testid={`deposit-tab-${t.key}`}
                    onClick={() => setStatus(t.key)}
                    className={`text-xs uppercase tracking-widest font-semibold px-3 py-2 rounded-lg border transition-colors ${
                      active
                        ? "bg-emerald-400 text-black border-emerald-400"
                        : "border-white/10 text-white/80 hover:bg-white/5"
                    }`}
                  >
                    {t.label} · <span className="font-mono">{counts[t.key] ?? 0}</span>
                  </button>
                );
              })}
            </div>
            <div className="relative w-full sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                data-testid="admin-deposit-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search UTR / user / ID..."
                className="w-full bg-black/50 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 focus:ring-2 focus:ring-emerald-400/60 outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/40 text-white/60 uppercase text-[11px] tracking-wider">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-left px-4 py-3">UTR</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-left px-4 py-3">Reviewed</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody data-testid="admin-deposits-table" className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-white/60">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-white/60">No {status} deposits.</td></tr>
                ) : items.map((d) => (
                  <tr key={d.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{d.user_name}</div>
                      <div className="text-xs text-emerald-400 font-mono">{d.user_code}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/10 bg-white/5">
                        {d.method === "qr" ? "QR" : "Bank"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{d.utr_number}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-400">
                        <IndianRupee size={12} />
                        {Number(d.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-white/70">{formatDate(d.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-white/70">{formatDate(d.verified_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {d.status === "pending" ? (
                        <div className="inline-flex gap-2">
                          <button
                            data-testid={`verify-${d.id}`}
                            onClick={() => decide(d.id, "verify")}
                            className="inline-flex items-center gap-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-3 py-1.5 rounded-lg"
                          >
                            <CheckCircle2 size={14} /> Verify
                          </button>
                          <button
                            data-testid={`reject-${d.id}`}
                            onClick={() => decide(d.id, "reject")}
                            className="inline-flex items-center gap-1 text-xs border border-red-500/50 text-red-400 hover:bg-red-500/20 px-3 py-1.5 rounded-lg"
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      ) : d.status === "verified" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400 font-semibold">
                          <CheckCircle2 size={14} /> Credited
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-400 font-semibold">
                          <XCircle size={14} /> Rejected
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
